import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  CreateSalesFeaturePayload,
  CreateSalesInvoicePayload,
  PersistSelectionPayload,
  SalesCalculatorPreviewPayload,
  SalesCalculatorResult,
  SalesCalculatorSelection,
  SalesFeature,
  SalesFeatureQuery,
  SalesInvoice,
  SalesInvoiceTermsTemplate,
  SalesPricingAssumptions,
  SendSalesInvoicePayload,
  SettableInvoiceStatus,
  UpdateSalesFeaturePayload,
  UpdateSalesInvoicePayload,
  UpdateSalesPricingAssumptionsPayload,
  UpdateSalesTermsTemplatePayload,
} from '../types';

/**
 * osprey-api `/sales/*` (PE-2143 FE, PE-2142 backend). Every admin route is
 * TCS-staff-only (CollectiveOnlyGuard) + `SALES_PRICING` permission, enforced
 * server-side; this client only sends the bearer ApiFetch already attaches.
 * NOT tenant-scoped — sales pricing is platform-level, so no x-tenant-id matters.
 *
 * osprey-api proxies these bodies through untouched (`@Body() body: unknown`), so
 * the payload shapes here are order-service's DTOs — see ../types.ts.
 *
 * ⚠️ `ApiFetch.put` issues a PATCH on the wire (shared/utils/fetch.ts). Every
 * update route below IS a PATCH server-side, so `put` is correct here — unlike
 * contractTemplatesApi, none of these need `legacy_put`.
 */

const qs = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.append(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export const salesApi = {
  // ---- Feature catalogue (the calculator's price book) ----
  listFeatures: async (query: SalesFeatureQuery = {}): Promise<SalesFeature[]> => {
    const response = await ApiFetch.get<Enveloped<SalesFeature[]>>(
      `/sales/features${qs({ category: query.category, isActive: query.isActive })}`,
    );
    return unwrap(response);
  },
  getFeature: async (id: string): Promise<SalesFeature> => {
    const response = await ApiFetch.get<Enveloped<SalesFeature>>(`/sales/features/${id}`);
    return unwrap(response);
  },
  createFeature: async (data: CreateSalesFeaturePayload): Promise<SalesFeature> => {
    const response = await ApiFetch.post<Enveloped<SalesFeature>>('/sales/features', data);
    return unwrap(response);
  },
  updateFeature: async (id: string, data: UpdateSalesFeaturePayload): Promise<SalesFeature> => {
    const response = await ApiFetch.put<Enveloped<SalesFeature>>(`/sales/features/${id}`, data);
    return unwrap(response);
  },
  /** Soft-deactivate — features are never hard-deleted (quotes reference them). */
  deactivateFeature: async (id: string): Promise<SalesFeature> => {
    const response = await ApiFetch.put<Enveloped<SalesFeature>>(
      `/sales/features/${id}/deactivate`,
      {},
    );
    return unwrap(response);
  },

  // ---- Calculator ----
  /** Compute price points without persisting. Safe to call on every form change. */
  preview: async (data: SalesCalculatorPreviewPayload): Promise<SalesCalculatorResult> => {
    const response = await ApiFetch.post<Enveloped<SalesCalculatorResult>>(
      '/sales/calculator/preview',
      data,
    );
    return unwrap(response);
  },
  /** Freeze a run into a selection — the only thing an invoice can be built from. */
  persistSelection: async (data: PersistSelectionPayload): Promise<SalesCalculatorSelection> => {
    const response = await ApiFetch.post<Enveloped<SalesCalculatorSelection>>(
      '/sales/calculator/selections',
      data,
    );
    return unwrap(response);
  },
  getSelection: async (id: string): Promise<SalesCalculatorSelection> => {
    const response = await ApiFetch.get<Enveloped<SalesCalculatorSelection>>(
      `/sales/calculator/selections/${id}`,
    );
    return unwrap(response);
  },

  // ---- Sales invoices ----
  listInvoices: async (): Promise<SalesInvoice[]> => {
    const response = await ApiFetch.get<Enveloped<SalesInvoice[]>>('/sales/invoices');
    return unwrap(response);
  },
  getInvoice: async (id: string): Promise<SalesInvoice> => {
    const response = await ApiFetch.get<Enveloped<SalesInvoice>>(`/sales/invoices/${id}`);
    return unwrap(response);
  },
  createInvoice: async (data: CreateSalesInvoicePayload): Promise<SalesInvoice> => {
    const response = await ApiFetch.post<Enveloped<SalesInvoice>>('/sales/invoices', data);
    return unwrap(response);
  },
  updateInvoice: async (id: string, data: UpdateSalesInvoicePayload): Promise<SalesInvoice> => {
    const response = await ApiFetch.put<Enveloped<SalesInvoice>>(`/sales/invoices/${id}`, data);
    return unwrap(response);
  },
  /** Emails the invoice link to the prospect. Outward-facing — confirm before firing. */
  sendToCustomer: async (id: string, data: SendSalesInvoicePayload): Promise<SalesInvoice> => {
    const response = await ApiFetch.post<Enveloped<{ invoice: SalesInvoice }>>(
      `/sales/invoices/${id}/send-to-customer`,
      data,
    );
    // This route nests the invoice under `invoice` alongside a messageId.
    const payload = unwrap(response) as { invoice: SalesInvoice } | SalesInvoice;
    return 'invoice' in payload ? payload.invoice : payload;
  },
  /** Only CANCELLED / VOID are accepted (backend @IsIn). */
  setInvoiceStatus: async (id: string, status: SettableInvoiceStatus): Promise<SalesInvoice> => {
    const response = await ApiFetch.put<Enveloped<SalesInvoice>>(`/sales/invoices/${id}/status`, {
      status,
    });
    return unwrap(response);
  },
  /** Invalidates the prospect's public link. */
  revokeToken: async (id: string): Promise<SalesInvoice> => {
    const response = await ApiFetch.post<Enveloped<SalesInvoice>>(
      `/sales/invoices/${id}/revoke-token`,
      {},
    );
    return unwrap(response);
  },
  downloadInvoicePdf: (id: string, invoiceNumber: string): Promise<void> =>
    ApiFetch.download(`/sales/invoices/${id}/pdf`, `${invoiceNumber}.pdf`),

  // ---- Config: pricing assumptions & terms template ----
  getAssumptions: async (): Promise<SalesPricingAssumptions> => {
    const response = await ApiFetch.get<Enveloped<SalesPricingAssumptions>>('/sales/assumptions');
    return unwrap(response);
  },
  updateAssumptions: async (
    data: UpdateSalesPricingAssumptionsPayload,
  ): Promise<SalesPricingAssumptions> => {
    const response = await ApiFetch.put<Enveloped<SalesPricingAssumptions>>(
      '/sales/assumptions',
      data,
    );
    return unwrap(response);
  },
  getTermsTemplate: async (): Promise<SalesInvoiceTermsTemplate> => {
    const response = await ApiFetch.get<Enveloped<SalesInvoiceTermsTemplate>>(
      '/sales/terms-template',
    );
    return unwrap(response);
  },
  updateTermsTemplate: async (
    data: UpdateSalesTermsTemplatePayload,
  ): Promise<SalesInvoiceTermsTemplate> => {
    const response = await ApiFetch.put<Enveloped<SalesInvoiceTermsTemplate>>(
      '/sales/terms-template',
      data,
    );
    return unwrap(response);
  },
};
